import { Search } from 'lucide-react';
import * as React from 'react';

import { FieldContext, type FieldControlContext } from '@/components/ui/field-context';
import { Input, type InputProps } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/* Field : libellé, contrôle, aide, erreur                                    */
/* -------------------------------------------------------------------------- */

interface FieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  label: React.ReactNode;
  /** Identifiant du contrôle. Généré si absent. */
  id?: string;
  /** Aide sous le champ (`field-hint`). */
  hint?: React.ReactNode;
  /** Message d'erreur (`field-error`) : ce qui ne va pas et comment corriger. */
  error?: React.ReactNode;
  /** Ajoute l'astérisque « * » au libellé (visuel seulement). */
  required?: boolean;
  /** Masque le libellé visuellement (il reste lu par les lecteurs d'écran). */
  hideLabel?: boolean;
}

/**
 * `field` des maquettes. Le contrôle enfant (Input, Textarea, NativeSelect)
 * reçoit automatiquement `id`, `aria-describedby` (aide + erreur) et
 * `aria-invalid` ; pour un autre contrôle, lire `useFieldControl` ou passer
 * `id` à la main.
 */
function Field({
  label,
  id,
  hint,
  error,
  required = false,
  hideLabel = false,
  className,
  children,
  ...props
}: FieldProps): React.JSX.Element {
  const generated = React.useId();
  const controlId = id ?? `field-${generated}`;
  const hasHint = hint !== undefined && hint !== null && hint !== false;
  const hasError = error !== undefined && error !== null && error !== false && error !== '';
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const describedBy = [hasHint ? hintId : null, hasError ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ');
  const context: FieldControlContext = {
    id: controlId,
    describedBy: describedBy.length > 0 ? describedBy : undefined,
    invalid: hasError,
  };
  return (
    <FieldContext.Provider value={context}>
      <div className={cn('grid min-w-0 gap-1.5', className)} {...props}>
        <Label htmlFor={controlId} className={hideLabel ? 'sr-only' : undefined}>
          {label}
          {required ? <span aria-hidden="true"> *</span> : null}
        </Label>
        {children}
        {hasHint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
        {hasError ? <FieldError id={errorId}>{error}</FieldError> : null}
      </div>
    </FieldContext.Provider>
  );
}

function FieldHint({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn('text-caption text-muted-foreground', className)} {...props} />;
}

/** Erreur : teinte `danger-fg` + losange, jamais la teinte seule. */
function FieldError({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      role="alert"
      className={cn('flex items-center gap-1.5 text-caption text-danger-fg', className)}
      {...props}
    >
      <Diamond />
      <span>{children}</span>
    </div>
  );
}

/** Losange de 8px en `currentColor` (forme du statut danger). */
function Diamond({ className }: { className?: string }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block size-2 shrink-0 bg-current [clip-path:polygon(50%_0,100%_50%,50%_100%,0_50%)]',
        className,
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Fieldset, fields-2                                                         */
/* -------------------------------------------------------------------------- */

interface FieldsetProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
}

/** `fieldset` + `fieldset-title` : groupe de champs encadré dans un dialogue. */
function Fieldset({
  title,
  description,
  className,
  children,
  ...props
}: FieldsetProps): React.JSX.Element {
  const titleId = React.useId();
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={cn('grid min-w-0 gap-3 rounded-lg border border-border bg-card p-4', className)}
      {...props}
    >
      <div>
        <div id={titleId} className="text-[13px] font-semibold leading-[18px]">
          {title}
        </div>
        {description !== undefined && description !== null ? (
          <p className="m-0 mt-0.5 text-caption text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** `fields-2` : deux colonnes de champs, une seule sous ~420px de large. */
function Fields2({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        'grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-4',
        className,
      )}
      {...props}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* input-group (suffixe), recherche                                           */
/* -------------------------------------------------------------------------- */

interface InputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Unité affichée à droite dans le champ : « € », « % », « $ ». */
  suffix: React.ReactNode;
}

/**
 * `input-group` + `suffix` : l'enfant `Input` reçoit la place du suffixe à
 * droite, élargie pour une unité en toutes lettres (« tokens »).
 */
function InputGroup({ suffix, className, children, ...props }: InputGroupProps): React.JSX.Element {
  const longSuffix = typeof suffix === 'string' && suffix.length > 2;
  return (
    <div
      className={cn(
        'relative min-w-0',
        longSuffix ? '[&_input]:pr-16' : '[&_input]:pr-9',
        className,
      )}
      {...props}
    >
      {children}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[13px] leading-[18px] text-muted-foreground"
      >
        {suffix}
      </span>
    </div>
  );
}

interface SearchInputProps extends Omit<InputProps, 'type'> {
  /** Libellé lu par les lecteurs d'écran (le placeholder n'en tient pas lieu). */
  label: string;
  wrapperClassName?: string;
}

/** `search` des barres de tableau : icône loupe, 28px, 260px de large au plus. */
const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ label, wrapperClassName, className, ...props }, ref) => (
    <label className={cn('relative block w-[260px] max-w-full', wrapperClassName)}>
      <span className="sr-only">{label}</span>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        ref={ref}
        type="search"
        controlSize="sm"
        className={cn('pl-8', className)}
        {...props}
      />
    </label>
  ),
);
SearchInput.displayName = 'SearchInput';

/* -------------------------------------------------------------------------- */
/* Case à cocher native, check-row                                            */
/* -------------------------------------------------------------------------- */

const nativeCheckboxClass = [
  'peer relative inline-grid size-4 shrink-0 cursor-pointer appearance-none place-content-center rounded-sm border border-input bg-background',
  'checked:border-primary checked:bg-primary',
  "checked:after:block checked:after:h-1 checked:after:w-2 checked:after:-translate-y-px checked:after:-rotate-45 checked:after:border-b-2 checked:after:border-l-2 checked:after:border-primary-foreground checked:after:content-['']",
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

/**
 * Case à cocher native (`check` des maquettes), compatible `register` de
 * react-hook-form. Pour un état contrôlé Radix, voir `checkbox.tsx`.
 */
const NativeCheckbox = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'input'>, 'type'>
>(({ className, ...props }, ref) => (
  <input ref={ref} type="checkbox" className={cn(nativeCheckboxClass, className)} {...props} />
));
NativeCheckbox.displayName = 'NativeCheckbox';

interface CheckRowProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  label: React.ReactNode;
  /** Clé technique affichée en mono à droite du libellé (`key`). */
  code?: React.ReactNode;
  /** Aide sous le libellé. */
  hint?: React.ReactNode;
}

/** `check-row` : case à cocher + libellé (+ clé technique), toute la ligne est cliquable. */
const CheckRow = React.forwardRef<HTMLInputElement, CheckRowProps>(
  ({ label, code, hint, className, ...props }, ref) => (
    <label
      className={cn('flex cursor-pointer items-start gap-2 text-[13px] leading-[18px]', className)}
    >
      <NativeCheckbox ref={ref} className="mt-px" {...props} />
      <span className="grid min-w-0 gap-0.5">
        <span className="flex flex-wrap items-baseline gap-2">
          <span>{label}</span>
          {code !== undefined && code !== null ? (
            <span className="font-mono text-xs text-muted-foreground">{code}</span>
          ) : null}
        </span>
        {hint !== undefined && hint !== null ? (
          <span className="text-caption text-muted-foreground">{hint}</span>
        ) : null}
      </span>
    </label>
  ),
);
CheckRow.displayName = 'CheckRow';

export {
  Field,
  FieldHint,
  FieldError,
  Diamond,
  Fieldset,
  Fields2,
  InputGroup,
  SearchInput,
  NativeCheckbox,
  CheckRow,
};
